import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAdminKycRequests } from '@/api/kyc.ts';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle, Clock, ShieldCheck, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { GenericDataTable } from '@/components/shared/tables';
import { Column } from '@/components/ui/data-table';
import { KycRequest, PaginatedResponse } from '@/models/kyc.ts';
import { type FilterPreset } from '@/components/shared/filters';
import { useSnackbar } from 'notistack';
import { useExportKycRequests } from '../../api/export/use-export';
import { useUrlState } from '@/hooks/shared';
import { PageContainer, PageHeader, StatTile, StatTileGrid } from '@/components/shared/layout';
import { EmptyState } from '@/components/shared/EmptyState';
import { StatusBadge } from '@/components/shared/StatusBadge';

const KycManagementPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const customer_id = searchParams.get('customer_id');

    // Use URL-based state management
    const {
        filters,
        sort,
        pagination,
        updateFilters,
        clearFilters,
        toggleSort,
        goToPage,
        updatePagination,
    } = useUrlState({
        sort: { key: 'submitted_at', direction: 'desc' },
        pagination: { page: 1, pageSize: 25 },
    });

    const [selectedItems, setSelectedItems] = useState<string[]>([]);

    const {
        data: kycRequests,
        isLoading,
        error,
    } = useQuery<PaginatedResponse<KycRequest>>({
        queryKey: [
            'adminKycRequests',
            customer_id,
            pagination.page,
            pagination.pageSize,
            filters,
            sort,
        ],
        queryFn: () => getAdminKycRequests(customer_id || undefined),
    });

    // Apply client-side filtering
    const filteredData = useMemo(() => {
        if (!kycRequests?.data) return [];

        let filtered = kycRequests.data;

        // Apply search filter
        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter(
                (request: KycRequest) =>
                    request.customer.name.toLowerCase().includes(searchTerm) ||
                    request.customer.email.toLowerCase().includes(searchTerm) ||
                    request.customer.id.toLowerCase().includes(searchTerm)
            );
        }

        // Apply status filter
        if (filters.status && filters.status !== 'all') {
            filtered = filtered.filter((request: KycRequest) => request.status === filters.status);
        }

        // Apply date filters
        if (filters.submitted_after && filters.submitted_after !== 'all') {
            filtered = filtered.filter(
                (request: KycRequest) =>
                    request.submitted_at &&
                    new Date(request.submitted_at) >= new Date(filters.submitted_after!)
            );
        }

        if (filters.submitted_before && filters.submitted_before !== 'all') {
            filtered = filtered.filter(
                (request: KycRequest) =>
                    request.submitted_at &&
                    new Date(request.submitted_at) <= new Date(filters.submitted_before!)
            );
        }

        if (filters.reviewed_after && filters.reviewed_after !== 'all') {
            filtered = filtered.filter(
                (request: KycRequest) =>
                    request.reviewed_at &&
                    new Date(request.reviewed_at) >= new Date(filters.reviewed_after!)
            );
        }

        if (filters.reviewed_before && filters.reviewed_before !== 'all') {
            filtered = filtered.filter(
                (request: KycRequest) =>
                    request.reviewed_at &&
                    new Date(request.reviewed_at) <= new Date(filters.reviewed_before!)
            );
        }

        // Apply has documents filter
        if (filters.has_documents && filters.has_documents !== 'all') {
            const hasDocuments = filters.has_documents === 'true';
            filtered = filtered.filter((request: KycRequest) =>
                hasDocuments
                    ? request.documents?.length > 0
                    : !request.documents || request.documents.length === 0
            );
        }

        return filtered;
    }, [kycRequests?.data, filters]);

    const handleRowClick = (rowData: KycRequest) => {
        console.log('Row clicked, row data:', rowData);
        const kycId = rowData.id;
        console.log('Navigating to KYC details:', kycId);
        navigate(`/kyc-requests/${kycId}`);
    };

    // Define filter fields for the GenericDataTable
    const filterFields = useMemo(
        () => [
            {
                key: 'search',
                label: 'Search',
                type: 'text' as const,
                placeholder: 'Search customers...',
            },
            {
                key: 'status',
                label: 'Status',
                type: 'select' as const,
                options: [
                    { value: 'pending', label: 'Pending' },
                    { value: 'approved', label: 'Approved' },
                    { value: 'rejected', label: 'Rejected' },
                ],
            },
            { key: 'submitted_after', label: 'Submitted After', type: 'date' as const },
            { key: 'submitted_before', label: 'Submitted Before', type: 'date' as const },
            { key: 'reviewed_after', label: 'Reviewed After', type: 'date' as const },
            { key: 'reviewed_before', label: 'Reviewed Before', type: 'date' as const },
            {
                key: 'has_documents',
                label: 'Has Documents',
                type: 'boolean' as const,
                options: [
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' },
                ],
            },
        ],
        []
    );

    // Save filter preset
    const handleSavePreset = (preset: Omit<FilterPreset, 'id'>) => {
        enqueueSnackbar(`Filter preset "${preset.name}" saved`, { variant: 'success' });
    };

    // Filter presets
    const filterPresets: FilterPreset[] = useMemo(
        () => [
            {
                id: 'pending-kyc',
                name: 'Pending KYC',
                description: 'KYC requests awaiting review',
                isDefault: true,
                filters: {
                    status: 'pending',
                },
            },
            {
                id: 'approved-kyc',
                name: 'Approved KYC',
                description: 'Successfully approved KYC requests',
                isDefault: true,
                filters: {
                    status: 'approved',
                },
            },
            {
                id: 'rejected-kyc',
                name: 'Rejected KYC',
                description: 'Rejected KYC requests',
                isDefault: true,
                filters: {
                    status: 'rejected',
                },
            },
            {
                id: 'recent-submissions',
                name: 'Recent Submissions',
                description: 'KYC requests submitted in the last 7 days',
                isDefault: true,
                filters: {
                    submitted_after: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                        .toISOString()
                        .split('T')[0],
                },
            },
        ],
        []
    );

    // Define columns for the DataTable
    const columns: Column<KycRequest>[] = useMemo(
        () => [
            {
                id: 'customer',
                label: 'Customer',
                key: 'customer',
                sortable: true,
                width: '25%',
                render: (_value, request) => (
                    <div>
                        <Link
                            to={`/kyc-requests/${request.id}`}
                            className="font-medium text-foreground hover:text-primary hover:underline"
                        >
                            {request.customer?.name}
                        </Link>
                        <div className="text-sm text-muted-foreground">
                            ID: {request.customer?.id}
                        </div>
                    </div>
                ),
            },
            {
                id: 'email',
                label: 'Email',
                key: 'customer_id',
                sortable: true,
                width: '20%',
                render: (_value, request) => request.customer?.email,
            },
            {
                id: 'status',
                label: 'Status',
                key: 'status',
                sortable: true,
                width: '15%',
                render: (_value, request) => (
                    <div className="space-y-1">
                        <StatusBadge status={request.status} kind="kyc" />
                        {request.admin_override && (
                            <div className="rounded border border-warning/20 bg-warning/10 px-2 py-1 text-xs text-warning">
                                Admin Override
                            </div>
                        )}
                    </div>
                ),
            },
            {
                id: 'submitted_at',
                label: 'Submitted',
                key: 'submitted_at',
                sortable: true,
                width: '15%',
                render: (_value, request) => (
                    <div className="text-sm">
                        <div>{format(new Date(request.submitted_at), 'MMM dd, yyyy')}</div>
                        <div className="text-muted-foreground">
                            {format(new Date(request.submitted_at), 'HH:mm')}
                        </div>
                    </div>
                ),
            },
            {
                id: 'documents',
                label: 'Documents',
                key: 'documents',
                sortable: false,
                width: '10%',
                render: (_value, request) => (
                    <div className="text-sm">
                        <span className="font-medium">{request.documents?.length || 0}</span>
                        <span className="text-muted-foreground"> documents</span>
                    </div>
                ),
            },
            {
                id: 'reviewer',
                label: 'Reviewer',
                key: 'reviewer',
                sortable: true,
                width: '15%',
                render: (_value, request) =>
                    request.reviewer ? (
                        <div className="text-sm">
                            <div className="font-medium">{request.reviewer.name}</div>
                            <div className="text-muted-foreground">
                                {request.reviewed_at &&
                                    format(new Date(request.reviewed_at), 'MMM dd, yyyy')}
                            </div>
                        </div>
                    ) : (
                        <span className="text-muted-foreground text-sm">Not reviewed</span>
                    ),
            },
        ],
        []
    );

    // Handler functions
    const handleFilterChange = (newFilters: Record<string, any>) => {
        updateFilters(newFilters);
    };

    const handleClearFilters = () => {
        clearFilters();
    };

    /** Drives the empty state: "no matches" reads differently from "nothing yet". */
    const hasActiveFilters = useMemo(
        () => Object.values(filters ?? {}).some(v => v !== '' && v !== null && v !== undefined),
        [filters]
    );

    const handleSort = (key: keyof KycRequest) => {
        toggleSort(key as string);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        updatePagination({ page: 1, pageSize: newPageSize });
    };

    const exportKycMutation = useExportKycRequests(
        () => {
            enqueueSnackbar('KYC requests exported successfully', { variant: 'success' });
        },
        (error: Error) => {
            console.error('Export failed:', error);
            enqueueSnackbar('Failed to export KYC requests', { variant: 'error' });
        }
    );

    const handleExport = () => {
        const exportFilters =
            selectedItems.length > 0 ? { selected_ids: selectedItems } : { ...filters };

        exportKycMutation.mutate({
            format: 'csv',
            filters: exportFilters,
        });
    };

    const getStatusCounts = () => {
        if (!kycRequests?.data) return { pending: 0, approved: 0, rejected: 0 };

        return kycRequests.data.reduce(
            (acc: any, request: KycRequest) => {
                acc[request.status] = (acc[request.status] || 0) + 1;
                return acc;
            },
            { pending: 0, approved: 0, rejected: 0 }
        );
    };

    const statusCounts = getStatusCounts();

    if (error) {
        return (
            <PageContainer>
                <PageHeader
                    title="KYC"
                    description="Review and manage customer KYC verification requests"
                />
                <p className="text-center text-destructive">
                    Error loading KYC requests: {error.message}
                </p>
            </PageContainer>
        );
    }

    return (
        <PageContainer fullHeight>
            <PageHeader
                title="KYC"
                description="Review and manage customer KYC verification requests"
            />

            <StatTileGrid className="shrink-0">
                <StatTile
                    label="Pending"
                    value={statusCounts.pending}
                    icon={Clock}
                    tone="warning"
                    isLoading={isLoading}
                />
                <StatTile
                    label="Approved"
                    value={statusCounts.approved}
                    icon={CheckCircle}
                    tone="success"
                    isLoading={isLoading}
                />
                <StatTile
                    label="Rejected"
                    value={statusCounts.rejected}
                    icon={XCircle}
                    tone="destructive"
                    isLoading={isLoading}
                />
            </StatTileGrid>

            {/* Generic Data Table */}
            <GenericDataTable<KycRequest>
                data={filteredData}
                columns={columns}
                visibleColumns={columns.map(col => col.id)}
                filters={filters}
                filterFields={filterFields}
                presets={filterPresets}
                isLoading={isLoading}
                selectedItems={selectedItems}
                onSelectionChange={setSelectedItems}
                getItemId={(request: KycRequest) => String(request.id)}
                sortConfig={{
                    key: sort.key as keyof KycRequest,
                    direction: sort.direction,
                }}
                onSort={handleSort}
                totalItems={kycRequests?.meta?.total || filteredData.length}
                totalPages={kycRequests?.meta?.last_page || 1}
                currentPage={pagination.page}
                pageSize={pagination.pageSize}
                onPageChange={goToPage}
                onPageSizeChange={handlePageSizeChange}
                onExport={handleExport}
                onRowClick={handleRowClick}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                onLoadPreset={preset => handleFilterChange(preset.filters)}
                onSavePreset={handleSavePreset}
                onColumnToggle={() => {}}
                onResetColumns={() => {}}
                emptyMessage="No KYC requests found"
                loadingMessage="Loading requests…"
                showExportButton={true}
                showRowClick={true}
                stickyLayout
                emptyState={
                    <EmptyState
                        icon={ShieldCheck}
                        title="No KYC requests found"
                        description={
                            hasActiveFilters
                                ? 'No request matches the current filters. Try clearing them.'
                                : 'KYC requests will appear here once customers submit verification.'
                        }
                        action={
                            hasActiveFilters ? (
                                <Button variant="outline" size="sm" onClick={handleClearFilters}>
                                    Clear filters
                                </Button>
                            ) : undefined
                        }
                    />
                }
            />
        </PageContainer>
    );
};

export default KycManagementPage;
